<template>
  <div class="home-wn-pcu-panel">
    <navigation-skip-links :show-quick-links="!isMobileOnDesktop" />
    <home-search-left-panel class="pcu-panel"
                            :title="$t('HEADER.PRACTICE_CHANGING_UPDATES')"
                            anchor-id="homepage_pcupanel_viewall"
                            :panel-button="{
                              url: '/contents/practice-changing-updates',
                              isVisible: true }">
      <div v-if="status.pcu.isLoading"
           class="home-panel-loading">
        <span class="wkce-icon-filled-spinner-alt wk-spin" />
      </div>
      <div v-else-if="status.pcu.isError"
           class="home-panel-error wkce-icon-filled-caution"
           role="alert">
        Content could not be loaded
      </div>
      <div v-else-if="status.pcu.isEmpty" class="home-panel-empty">
        No recent Practice Changing Updates
      </div>
      <ul v-else
          class="panel-content"
          role="list"
          aria-labelledby="pcu-panel"
          aria-live="polite">
        <li v-for="(item, index) in panelContent.pcuPanelInfo"
            :key="index"
            class="home-panel-list-item"
            role="listitem">
          <span class="home-panel-category">{{ item.category }}:</span>
          <a id="homepage_pcupanel"
             class="home-panel-link"
             :href="item.url"
             :aria-label="`${item.category} ${item.title}`">{{ item.title }}</a>
          <span class="home-panel-date">({{ formatDate(item.date, item.modifiedDate) }})</span>
        </li>
      </ul>
    </home-search-left-panel>
    <home-search-left-panel class="wn-panel"
                            :title="$t('HEADER.WHATS_NEW')"
                            anchor-id="homepage_whatsnewpanel_viewall"
                            :panel-button="{
                              url: '/contents/table-of-contents/whats-new',
                              isVisible: true }"
                            :panel-filter="{
                              isVisible: true,
                              options: panelContent?.wnSpecialtyList,
                              selected: defaultSelectedSpecialty
                            }"
                            @panel-filter-change="handlePanelFilterChange">
      <div v-if="status.wn.isLoading"
           class="home-panel-loading">
        <span class="wkce-icon-filled-spinner-alt wk-spin" />
      </div>
      <div v-else-if="status.wn.isError"
           class="home-panel-error wkce-icon-filled-caution"
           role="alert">
        Content could not be loaded
      </div>
      <span v-else-if="status.wn.isEmpty && panelContent.selectedSpecialtyId"
            class="home-panel-empty">
        No recent What's New in this specialty
      </span>
      <ul v-else
          class="panel-content"
          role="list"
          aria-labelledby="wn-panel"
          aria-live="polite">
        <li v-for="(item, index) in panelContent.wnPanelInfo"
            :key="index"
            class="home-panel-list-item"
            role="listitem">
          <span class="home-panel-category">{{ item.category }}:</span>
          <a class="home-panel-link"
             :href="item.url"
             :aria-label="`${item.category} ${item.title}`"
             @click="logUiClickEvent('homepage_whatsnewpanel', item.url)">{{ item.title }}</a>
          <span class="home-panel-date">({{ formatDate(item.date, item.modifiedDate) }})</span>
        </li>
      </ul>
    </home-search-left-panel>
  </div>
</template>

<script>
import HomeSearchLeftPanel from '_acaSrc/components/homePageRedesign/HomeSearchLeftPanel.vue';
import {mapGetters, mapActions, mapMutations} from 'vuex';
import { logUiClickEventHelper } from '_acaSrc/utility/Events';
import NavigationSkipLinks from '../header/NavigationSkipLinks.vue';
import { SET_WN_SPECIALTY_ID } from '_acaSrc/store/topic/topic.store';
import WkceDates from '_acaSrc/utility/Dates';

function makeInitialStatus() {
    return {
        isLoading: false,
        isError: false,
        isEmpty: false
    };
}

export default {
    name: 'HomeWnPcuPanel',
    components: {
        HomeSearchLeftPanel,
        NavigationSkipLinks
    },
    data() {
        return {
            panelContent: {
                pcuPanelInfo: [],
                wnPanelInfo: [],
                wnSpecialtyList: [],
                selectedSpecialtyId: ''
            },
            status: {
                pcu: makeInitialStatus(),
                wn: makeInitialStatus()
            },
            fetchTimeout: null
        };
    },
    computed: {
        ...mapGetters('feature', [ 'isHeaderRedesign', 'isWnPcuPanelEnabled' ]),
        ...mapGetters('app', [ 'isUccState', 'isProspectView' ]),
        ...mapGetters('topic', [ 'wnPcuPanelInfo' ]),
        ...mapGetters('device', [ 'isMobileOnDesktop' ]),
        defaultSelectedSpecialty() {
            if (this.panelContent?.selectedSpecialtyId === '') {
                return null;
            }
            return this.panelContent?.wnSpecialtyList?.findIndex(
                specialty => specialty.value === this.panelContent.selectedSpecialtyId
            );
        }
    },
    watch: {
        wnPcuPanelInfo: {
            handler() {
                this.panelContent = {
                    ...this.panelContent, ...this.wnPcuPanelInfo};
            },
            deep: true,
            immediate: true
        }
    },
    async mounted() {
        if (this.isWnPcuPanelEnabled &&
            (this.isInfoEmpty(this.panelContent?.pcuPanelInfo) ||
            this.isInfoEmpty(this.panelContent?.wnPanelInfo))) {
            await this.fetchWnPcuPanelContent(this.fetchWnPcuInfo);
        }
    },
    methods: {
        ...mapActions('topic', [ 'fetchUpdatedWnInfo', 'fetchWnPcuInfo' ]),
        ...mapMutations('topic', [ SET_WN_SPECIALTY_ID ]),
        formatDate(date, modifiedDate) {
            if (modifiedDate && date) {
                // eslint-disable-next-line max-len
                return `${WkceDates.formatToMonthYear(date)}, Modified ${WkceDates.formatToMonthYear(modifiedDate)}`;
            }
            return WkceDates.formatToMonthYear(date);
        },
        isInfoEmpty(panelArray) {
            return !panelArray || panelArray.length === 0;
        },
        async fetchWnPcuPanelContent(panelInfo) {
            this.resetStatus();
            this.startLoading(panelInfo);
            try {
                await panelInfo(arguments[1]);
                const { pcuPanelInfo, wnPanelInfo } = this.panelContent;
                const isPcuEmpty = this.isInfoEmpty(pcuPanelInfo);
                const isWnEmpty = this.isInfoEmpty(wnPanelInfo);
                this.updateStatus(panelInfo, 'isEmpty',
                    { pcuValue: isPcuEmpty, wnValue: isWnEmpty });
                this.updateStatus(panelInfo, 'isError', { pcuValue: false, wnValue: false });
            }
            catch {
                this.updateStatus(panelInfo, 'isError', { pcuValue: true, wnValue: true });
            }
            finally {
                this.stopLoading(panelInfo);
            }
        },
        startLoading(panelInfo) {
            this.fetchTimeout = setTimeout(() => {
                this.updateStatus(panelInfo, 'isLoading', { pcuValue: true, wnValue: true });
            }, 2000);
        },
        stopLoading(panelInfo) {
            clearTimeout(this.fetchTimeout);
            this.updateStatus(panelInfo, 'isLoading', { pcuValue: false, wnValue: false });
        },
        updateStatus(panelInfo, key, values) {
            const { pcuValue, wnValue } = values;
            if (panelInfo === this.fetchWnPcuInfo) {
                this.status.pcu[key] = pcuValue;
            }
            this.status.wn[key] = wnValue;
        },
        resetStatus() {
            this.status.pcu = makeInitialStatus();
            this.status.wn = makeInitialStatus();
        },
        async handlePanelFilterChange(selected) {
            const selectedOption = this.panelContent?.wnSpecialtyList[selected];
            this.panelContent.wnPanelInfo = [];
            this.logUiClickEvent('homepage_whatsnewpanel_dropdown', null, selectedOption?.name);
            this.panelContent.selectedSpecialtyId = selectedOption ?
                selectedOption.value : this.panelContent.selectedSpecialtyId;
            this[SET_WN_SPECIALTY_ID](this.panelContent);
            await this.fetchWnPcuPanelContent(this.fetchUpdatedWnInfo,
                { specialtyId: this.panelContent.selectedSpecialtyId });
        },
        logUiClickEvent(element, url, opt) {
            logUiClickEventHelper({
                uiElementName: element,
                targetUrl: url,
                optData: opt
            });
        }
    }
};
</script>

<style scoped lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.home-panel-error,
.home-panel-empty {
  .ds1-mb-2();
  font-size: 14px;
  font-style: italic;
}

.home-panel-error {
    color: @DS1-UTD-ERROR;
    &::before {
        .ds1-mr-compact();
        position: relative;
        top: 2px;
        font-style: normal;
    }
}

.home-panel-empty {
  color: @DS1-UTD-SUB-TEXT-COLOR;
}

.home-panel-loading {
  .ds1-mb-2();
  display: flex;
  justify-content: center;
}

.home-wn-pcu-panel {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.isWnPcuPanelEnabled & {
  .wn-panel {
    flex-grow: 1;

    :deep(.home-panel-content) {
      .ds1-mb-1();
    }
  }

  .home-panel-category {
    font-size: 12px;
    color: @WKCE-GRAY;
    line-height: 21px;
    font-weight: 500;
  }

  .home-panel-link {
    color: @DS2-UTD-TOPIC-LINK-COLOR;
    font-size: 14px;
    line-height: 21px;
    font-weight: 400;
  }

  .home-panel-date {
    color: @DS1-UTD-SUB-TEXT-COLOR;
    font-size: 14px;
    line-height: 21px;
    font-weight: 400;
  }

  .home-panel-list-item {
    .ds1-mb-2();
  }

  .panel-content {
    .ds1-pl-0();
    .ds1-mt-0();
    .ds1-mb-0();
    list-style-type: none;
  }
}

</style>